import React from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon, 
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  Avatar,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  LocalLibrary,
  Logout,
  AccountCircle,
  CreditCard,
  AccountBox,
  AutoAwesome,
  Collections
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { useBook } from '../../Context/BookContext';

const drawerWidth = 260; // Slightly wider for better content display

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  color?: string;
}

const menuItems: MenuItem[] = [
  { text: 'Library', icon: <LocalLibrary />, path: '/' },
  { text: 'Examples', icon: <Collections />, path: '/examples' },
  { text: 'Credits', icon: <CreditCard />, path: '/credits' },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen = false, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOutUser } = useAuth();
  const { availableImages, isLoadingCredits, errorCredits } = useBook();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/');
      if (isMobile && onMobileClose) {
        onMobileClose();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Redirect to credits page for purchases
  const handlePurchaseCredits = () => {
    navigate('/credits');
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const drawerContent = (
    <>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <AutoAwesome sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700 }}>
            TextWiz
          </Typography>
        </Box>
        
        {/* Credits Display */}
        <Card sx={{ 
          backgroundColor: 'action.hover', 
          border: '1px solid',
          borderColor: 'action.disabled',
          boxShadow: 'none'
        }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Credits
              </Typography>
              <CreditCard sx={{ color: 'warning.main', fontSize: 20 }} />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {isLoadingCredits ? (
                <Chip 
                  label="Loading..." 
                  size="small" 
                  sx={{ 
                    backgroundColor: 'action.disabled', 
                    color: 'text.secondary',
                    fontSize: '0.75rem'
                  }} 
                />
              ) : (
                <Chip 
                  label={availableImages !== null ? `${availableImages} available` : 'Error loading'} 
                  size="small"
                  sx={{ 
                    backgroundColor: availableImages && availableImages > 0 ? 'success.main' : 'error.main',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }} 
                />
              )}
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={handlePurchaseCredits}
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
                textTransform: 'none',
                fontWeight: 500,
                py: 0.5
              }}
            >
              Buy More Credits
            </Button>
            
            {errorCredits && (
              <Typography variant="caption" sx={{ 
                color: 'error.main', 
                mt: 0.5, 
                display: 'block',
                fontSize: '0.7rem'
              }}>
                {errorCredits}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
      
      {/* Navigation */}
      <List sx={{ flex: 1, pt: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 2, mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(65, 211, 189, 0.15)',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'text.primary',
                    fontWeight: 600,
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                },
                py: 1.5,
                px: 2,
              }}
            >
              <ListItemIcon sx={{ 
                color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname === item.path ? 600 : 500,
                  color: location.pathname === item.path ? 'text.primary' : 'text.secondary'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: 'divider', mx: 2 }} />
      
      {/* User Info and Actions */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
            <AccountCircle sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary', 
                fontSize: '0.85rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.email || 'User'}
            </Typography>
            {user?.emailVerified && (
              <Chip
                label="Verified"
                size="small"
                sx={{ 
                  backgroundColor: 'success.main',
                  color: '#fff',
                  height: 16,
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  mt: 0.5
                }}
              />
            )}
          </Box>
        </Box>
        
        <Stack spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<AccountBox />}
            onClick={() => handleNavigate('/account')}
            sx={{
              color: 'text.secondary',
              borderColor: 'action.disabled',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(65, 211, 189, 0.05)',
              },
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Account
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<Logout />}
            onClick={handleSignOut}
            sx={{
              color: 'text.secondary',
              borderColor: 'action.disabled',
              '&:hover': {
                borderColor: 'text.secondary',
                backgroundColor: 'action.hover',
              },
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Sign Out
          </Button>
        </Stack>
      </Box>
    </>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderRightColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderRightColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar; 